import heroBgWaveDark from "@/assets/hero-bg-wave-dark.webp";
import heroBgWaveLight from "@/assets/hero-bg-wave-light.webp";
import { useTheme } from "@/lib/theme-context";

/**
 * Shared wave background overlay used across all page heroes and the
 * bottom CTA sections, so the brand backdrop is uniform site-wide.
 *
 * Drop this as the first child of a `relative overflow-hidden` <section>.
 *
 * `fadeTop` / `fadeBottom` control where the vertical dissolve starts (in %).
 * The defaults intentionally fade early and slowly, so the artwork has fully
 * dissolved before the next section begins instead of ending on a clean edge.
 */
export default function HeroWave({
  opacity = 0.28,
  fadeTop = 10,
  fadeBottom = 62,
  edgeFade = true,
  edgeFadeTo,
  bleed = "none",
}: {
  opacity?: number;
  fadeTop?: number;
  fadeBottom?: number;
  edgeFade?: boolean;
  edgeFadeTo?: string;
  bleed?: "none" | "bottom" | "both";
}) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const fadeColor = edgeFadeTo ?? "hsl(var(--background))";

  // Vertical fade so the wave dissolves into the section background at the
  // top and bottom edges, blending seamlessly into adjacent sections
  // instead of ending in a hard horizontal seam.
  const fadeMask = `linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.65) ${Math.max(fadeTop - 4, 0)}%, rgba(0,0,0,1) ${fadeTop}%, rgba(0,0,0,1) ${fadeBottom}%, rgba(0,0,0,0.35) ${Math.min(fadeBottom + 22, 96)}%, transparent 100%)`;

  // Dark theme: near-black artwork with light particles, `screen`ed so the
  // bright particles glow over the ink background.
  // Light theme: a dedicated light artwork (soft brand waves on near-white),
  // `multiply`ed so the delicate ribbons sit naturally on the page without
  // the muddy look the inverted dark artwork produced.
  return (
    <>
      <div
        className="pointer-events-none absolute inset-x-0"
        aria-hidden="true"
        style={{
          top: bleed === "both" ? "-18%" : 0,
          bottom: bleed === "bottom" || bleed === "both" ? "-18%" : 0,
          backgroundImage: `url(${isLight ? heroBgWaveLight : heroBgWaveDark})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: isLight ? Math.min(opacity * 0.6, 0.2) : opacity,
          mixBlendMode: isLight ? "multiply" : "screen",
          maskImage: fadeMask,
          WebkitMaskImage: fadeMask,
        }}
      />
      {edgeFade && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-[-1px] h-[48%]"
          aria-hidden="true"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, color-mix(in oklab, ${fadeColor} 35%, transparent) 28%, color-mix(in oklab, ${fadeColor} 82%, transparent) 74%, ${fadeColor} 100%)`,
          }}
        />
      )}
    </>
  );
}
